import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Instagram,
  Github,
  Youtube,
  ArrowRight,
} from "lucide-react";
import { siteConfig, services, industryApproaches } from "@/lib/data";
import { useTranslation } from "react-i18next";
import logoText from "@/assets/logo_text.png";
export default function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-navy-950 border-t border-white/[0.06]">
      {/* CTA Banner */}
      <div className="section-container section-padding">
        <div className="relative -mt-24 mb-16 p-8 md:p-12 rounded-3xl bg-gradient-to-br from-neon-500/10 via-navy-800/50 to-navy-900/50 border border-neon-500/20 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-30" />
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                {t('home.footer.siap_memulai')}
              </h3>
              <p className="text-white/60 max-w-lg">
                {t('home.footer.konsultasikan_ide')}
              </p>
            </div>
            <Link to="/konsultasi" className="btn-primary whitespace-nowrap text-base">
              {t('home.footer.mulai_konsultasi')}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="section-container section-padding pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-5">
              <img
                src={logoText}
                alt="Naincode Logo"
                className="h-11 w-auto object-contain hover:brightness-110 transition-all"
              />
            </Link>
            <p className="text-white/50 text-sm leading-relaxed max-w-sm mb-6">
              {t('home.desc')}
            </p>
            <div className="space-y-3">
              <a
                href={`mailto:${siteConfig.email}`}
                className="flex items-center gap-3 text-sm text-white/50 hover:text-neon-400 transition-colors"
              >
                <Mail className="w-4 h-4" />
                {siteConfig.email}
              </a>
              <a
                href={`tel:${siteConfig.phone}`}
                className="flex items-center gap-3 text-sm text-white/50 hover:text-neon-400 transition-colors"
              >
                <Phone className="w-4 h-4" />
                {siteConfig.phone}
              </a>
              <div className="flex items-center gap-3 text-sm text-white/50">
                <MapPin className="w-4 h-4" />
                {siteConfig.address}
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">
              {t('home.footer.layanan')}
            </h4>
            <ul className="space-y-2.5">
              {(t('home.services', { returnObjects: true }) as any[]).map((service, index) => {
                const originalService = services[index] || services[0];
                return (
                  <li key={index}>
                    <Link
                      to={`/layanan/${originalService.slug}`}
                      className="text-sm text-white/40 hover:text-neon-400 transition-colors"
                    >
                      {service.shortTitle}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Industri */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">
              {t('home.footer.industri')}
            </h4>
            <ul className="space-y-2.5">
              {(t('home.industryApproaches', { returnObjects: true }) as any[]).map((industry, index) => {
                const originalInd = industryApproaches[index] || industryApproaches[0];
                return (
                  <li key={index}>
                    <Link
                      to={`/industri/${originalInd.slug}`}
                      className="text-sm text-white/40 hover:text-neon-400 transition-colors"
                    >
                      {industry.title}
                    </Link>
                  </li>
                );
              })}
              <li>
                <Link
                  to="/studi-kasus"
                  className="text-sm text-white/40 hover:text-neon-400 transition-colors"
                >
                  {t('home.footer.studi_kasus')}
                </Link>
              </li>
              <li>
                <Link
                  to="/produk"
                  className="text-sm text-white/40 hover:text-neon-400 transition-colors"
                >
                  {t('home.footer.produk')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">
              {t('home.footer.perusahaan')}
            </h4>
            <ul className="space-y-2.5">
              <li>
                <Link
                  to="/tentang"
                  className="text-sm text-white/40 hover:text-neon-400 transition-colors"
                >
                  {t('home.footer.tentang_kami')}
                </Link>
              </li>
              <li>
                <Link
                  to="/proses"
                  className="text-sm text-white/40 hover:text-neon-400 transition-colors"
                >
                  {t('home.footer.proses_kami')}
                </Link>
              </li>
              <li>
                <Link
                  to="/konsultasi"
                  className="text-sm text-white/40 hover:text-neon-400 transition-colors"
                >
                  {t('home.footer.konsultasi')}
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="text-sm text-white/40 hover:text-neon-400 transition-colors"
                >
                  {t('home.footer.blog_insight')}
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  className="text-sm text-white/40 hover:text-neon-400 transition-colors"
                >
                  {t('home.footer.faq')}
                </Link>
              </li>
            </ul>

            {/* Social */}
            <div className="flex items-center gap-3 mt-6">
              <a
                href={siteConfig.socials.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-white/40 hover:text-neon-400 hover:bg-neon-500/10 transition-all"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href={siteConfig.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-white/40 hover:text-neon-400 hover:bg-neon-500/10 transition-all"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={siteConfig.socials.github}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-white/40 hover:text-neon-400 hover:bg-neon-500/10 transition-all"
              >
                <Github className="w-4 h-4" />
              </a>
              <a
                href={siteConfig.socials.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-white/40 hover:text-neon-400 hover:bg-neon-500/10 transition-all"
              >
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="line-glow mb-6" />
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/30">
            &copy; {currentYear} {siteConfig.fullName}. {t('home.footer.all_rights_reserved')}
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="text-xs text-white/30 hover:text-white/60 transition-colors">
              {t('home.footer.privacy_policy')}
            </Link>
            <Link to="/terms-of-service" className="text-xs text-white/30 hover:text-white/60 transition-colors">
              {t('home.footer.terms_of_service')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
